// ✅ src/pages/VehicleV2/DayWiseDailyProgressReport.jsx
// Production listing template

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/dayWiseDailyProgressReport";

const toStr = (v) => String(v ?? "").trim();
const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const normalizeOptionList = (arr, fallbackSelect = true) => {
  const list = Array.isArray(arr) ? arr : [];
  const mapped = list.map((x) => {
    if (typeof x === "string") return { label: x, value: x };
    const label = x.label ?? x.fld_branchname ?? x.fld_BranchName ?? x.fld_vehicletypename ?? x.VehicleType ?? x.value ?? "";
    const value = x.value ?? x.fld_branchid ?? x.fld_vehiclemaintypeid ?? x.fld_vehicletypeindex ?? x.id ?? x.label ?? "";
    return { label: String(label), value: String(value) };
  });
  const hasSelect = mapped.some((o) => toStr(o.value).toLowerCase() === "select");
  return !fallbackSelect || hasSelect ? mapped : [{ label: "Select", value: "Select" }, ...mapped];
};

const monthOptions = [
  { label: "Select", value: "Select" },
  { label: "January", value: "1" }, { label: "February", value: "2" }, { label: "March", value: "3" },
  { label: "April", value: "4" }, { label: "May", value: "5" }, { label: "June", value: "6" },
  { label: "July", value: "7" }, { label: "August", value: "8" }, { label: "September", value: "9" },
  { label: "October", value: "10" }, { label: "November", value: "11" }, { label: "December", value: "12" },
];

const dayCols = Array.from({ length: 31 }, (_, i) => String(i + 1));

export default function DayWiseDailyProgressReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [mode, setMode] = useState("HOUR");
  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    vehicleTypes: [{ label: "Select", value: "Select" }],
    years: [{ label: "Select", value: "Select" }],
    months: monthOptions,
  });
  const [filters, setFilters] = useState({
    branch: sessionBranchType && sessionBranchType.toUpperCase() !== "HO" ? sessionBranchId || "Select" : "Select",
    month: "Select",
    year: "Select",
    vehicleType: "Select",
    vehicleCode: "",
    vehicleName: "",
    regNo: "",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingInit || loadingList;
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const loadInit = async () => {
    try {
      setLoadingInit(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        headers: apiHeaders,
        params: { branchId: sessionBranchId, branchType: sessionBranchType, loginUserId: userId },
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");
      const yearsRaw = (data.years || []).map((y) => String(y));
      const yearsOk = yearsRaw.filter((y) => Number(y) >= 1990);
      const years = normalizeOptionList((yearsOk.length ? yearsOk : yearsRaw).map((y) => ({ label: String(y), value: String(y) })));
      setDd((p) => ({
        ...p,
        branches: normalizeOptionList(data.branches),
        vehicleTypes: normalizeOptionList(data.vehicleTypes),
        years: years.length ? years : p.years,
        months: monthOptions,
      }));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load dropdowns");
    } finally {
      setLoadingInit(false);
    }
  };

  const loadList = async (override = {}, silent = false) => {
    setLoadingList(true);
    try {
      const f = { ...filters, ...override };
      const { data } = await axios.get(`${API_BASE}/list`, {
        params: {
          mode,
          branchId: sessionBranchId,
          branchType: sessionBranchType,
          loginUserId: userId,
          branch: isSelect(f.branch) ? "Select" : f.branch,
          month: isSelect(f.month) ? "Select" : f.month,
          year: isSelect(f.year) ? "Select" : f.year,
          vehicleType: isSelect(f.vehicleType) ? "Select" : f.vehicleType,
          vehicleCode: toStr(f.vehicleCode),
          vehicleName: toStr(f.vehicleName),
          regNo: toStr(f.regNo),
        },
        headers: apiHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "List failed");
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setTotal(Number(data.total || (data.rows?.length ?? 0)));
      setCurrentPage(1);
      if (!silent) toast.success("Search completed");
    } catch (err) {
      console.error(err);
      setRows([]);
      setTotal(0);
      toast.error(err?.message || "Failed to load report");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadInit(); }, []);
  useEffect(() => { if (rows.length) loadList({}, true); }, [mode]);

  const handleChange = (e) => setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleSearch = () => loadList();
  const handleViewAll = async () => {
    const reset = {
      branch: sessionBranchType && sessionBranchType.toUpperCase() !== "HO" ? sessionBranchId || "Select" : "Select",
      month: "Select", year: "Select", vehicleType: "Select",
      vehicleCode: "", vehicleName: "", regNo: "",
    };
    setFilters(reset);
    await loadList(reset, true);
    toast.info("Showing all records");
  };

  const handlePrintAll = () => {
    navigate("/vehicle/DayWiseDailyProgressReport_Print", {
      state: { mode, filters, apiHeaders, session: { finYearId, finYear, branchId: sessionBranchId, branchType: sessionBranchType, userId } },
    });
  };

  const getVehicleIdByCode = async (vehicleCode) => {
    const vc = toStr(vehicleCode);
    if (!vc) throw new Error("Vehicle Code is empty");
    const { data } = await axios.get(`${API_BASE}/vehicleIdByCode`, { params: { vehicleCode: vc }, headers: apiHeaders });
    if (!data?.success) throw new Error(data?.message || "vehicleIdByCode failed");
    const vehId = data?.vehicleId ?? data?.vehId ?? data?.id ?? 0;
    if (!vehId) throw new Error("Vehicle ID not found for this vehicle code");
    return String(vehId);
  };

  const handleVehicleWise = async (row) => {
    try {
      const vehicleCode = row?.VehicleCode ?? row?.vehicleCode ?? row?.fld_vehicle_code ?? "";
      const date = row?.Lastupdatedate ?? "";
      if (!toStr(vehicleCode)) return toast.error("Vehicle Code not found in row");
      const vehId = await getVehicleIdByCode(vehicleCode);
      navigate("/vehicle/DayWiseDailyProgressReportVehicleWise", {
        state: { vehId, vehicleCode: toStr(vehicleCode), progMonth: filters.month, progYear: filters.year, progDate: date, mode, filters, apiHeaders, session: { finYearId, finYear, branchId: sessionBranchId, branchType: sessionBranchType, userId } },
      });
    } catch (err) {
      toast.error(err?.message || "Failed to open Vehicle Wise report");
    }
  };

  const handleDayWise = (row, dayNo) => {
    const vehicleCode = row?.VehicleCode ?? row?.vehicleCode ?? row?.fld_vehicle_code ?? "";
    if (!toStr(vehicleCode)) return toast.error("Vehicle Code not found in row");
    navigate("/vehicle/DayWiseDailyProgressReport_DayWise", {
      state: { mode, filters, vehicleCode, dayNo: Number(dayNo), apiHeaders, session: { finYearId, finYear, branchId: sessionBranchId, branchType: sessionBranchType, userId } },
    });
  };

  const get = (r, keys, fallback = "") => {
    for (const k of keys) {
      const v = r?.[k];
      if (v !== null && v !== undefined && toStr(v) !== "") return v;
    }
    return fallback;
  };

  const pagedRows = rows.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar title={<span className="font-dmserif text-2xl md:text-3xl">DAY WISE DAILY PROGRESS REPORT</span>} setSkin={setSkin} currentSkin={skin} />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white" : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>Day Wise Daily Progress Report</h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrintAll} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}><FaPrint /> Print</button>
              <button onClick={loadInit} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSyncAlt /> Refresh</button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}><FaArrowLeft /> Back</button>
              <button onClick={() => setShowFilters((p) => !p)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          <div className="flex gap-6 mb-4 items-center">
            <label className="flex items-center gap-2"><input type="radio" checked={mode === "HOUR"} onChange={() => setMode("HOUR")} /> HOUR</label>
            <label className="flex items-center gap-2"><input type="radio" checked={mode === "KM"} onChange={() => setMode("KM")} /> KM</label>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0"><label className={labelClass}>Branch</label><select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass}>{dd.branches.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Month</label><select name="month" value={filters.month} onChange={handleChange} className={fieldClass}>{dd.months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Year</label><select name="year" value={filters.year} onChange={handleChange} className={fieldClass}>{dd.years.map((y) => <option key={y.value} value={y.value}>{y.label}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Type</label><select name="vehicleType" value={filters.vehicleType} onChange={handleChange} className={fieldClass}>{dd.vehicleTypes.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Code</label><input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle code" /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Name</label><input type="text" name="vehicleName" value={filters.vehicleName} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle name" /></div>
                <div className="min-w-0"><label className={labelClass}>Vehicle Registration No.</label><input type="text" name="regNo" value={filters.regNo} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle reg no" /></div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {total}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{loadingInit ? "Loading dropdowns..." : loadingList ? "Searching..." : "INFRA SCREEN : B-64"}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div></div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}><div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div></div>
            ) : (
              <table className={`w-max min-w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">S.No</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Branch</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Vehicle Code</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Reg No</th>
                    {dayCols.map((d) => <th key={d} className="px-2 py-3 text-left text-sm font-semibold whitespace-nowrap">{d}</th>)}
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Total HR</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">Total KM</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {pagedRows.map((r, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{get(r, ["Branch", "fld_branchname", "BranchName"])}</td>
                      <td className="px-3 py-3 text-sm whitespace-nowrap">
                        <button type="button" className={`underline font-semibold ${isDark ? "text-blue-300" : "text-blue-700"}`} onClick={() => handleVehicleWise(r)}>
                          {get(r, ["VehicleCode", "vehicleCode", "fld_vehicle_code"])}
                        </button>
                      </td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{get(r, ["VehicleRegNo", "regNo", "fld_vehicle_regno"])}</td>
                      {dayCols.map((d) => {
                        const val = r?.[d] ?? r?.[`D${d}`] ?? "";
                        const isClickable = toStr(val) !== "" && toStr(val) !== "0";
                        return (
                          <td key={d} className="px-2 py-3 text-sm whitespace-nowrap">
                            {isClickable ? (
                              <button type="button" className={`underline ${isDark ? "text-white" : "text-gray-900"}`} onClick={() => handleDayWise(r, d)}>{val}</button>
                            ) : (
                              <span className={isDark ? "text-gray-500" : "text-gray-400"}>{toStr(val) || ""}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{get(r, ["TotalHr", "TotalHR", "TotHr"])}</td>
                      <td className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>{get(r, ["TotalKM", "TotKM"])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}